import { apiClient, getErrorMessage } from '@/lib/apiClient';
import { JsonSchema, UISchemaElement } from '@jsonforms/core';

/**
 * Dynamic Field (UDF) interface
 */
export interface DynamicField {
  id: string;
  tenantId?: string | null;
  orgUnitId?: string | null;
  entityName: string;
  moduleName: string;
  fieldName: string;
  displayName: string;
  description?: string;
  fieldType: string;
  typeConfig?: Record<string, any>;
  validationRules?: Record<string, any>;
  width?: number;
  displayOrder?: number;
  section?: string;
  isVisible: boolean;
  isReadOnly: boolean;
  isActive: boolean;
  isIndexed: boolean;
  isSearchable: boolean;
  readPermission?: string;
  writePermission?: string;
  metadata?: Record<string, any>;
}

/**
 * Dynamic Field View interface
 */
export interface DynamicFieldView {
  id: string;
  dynamicFieldId: string;
  dynamicField: DynamicField;
  entityName: string;
  viewName: string;
  displayOrder: number;
  section?: string;
  isVisible: boolean;
  isRequired: boolean;
  visibilityRule?: Record<string, any>;
}

/**
 * Create Dynamic Field DTO
 */
export interface CreateDynamicField {
  entityName: string;
  fieldName: string;
  displayName: string;
  description?: string;
  fieldType: string;
  typeConfig?: Record<string, any>;
  validationRules?: Record<string, any>;
  width?: number;
  displayOrder?: number;
  section?: string;
  isVisible?: boolean;
  isReadOnly?: boolean;
  isIndexed?: boolean;
  isSearchable?: boolean;
  orgUnitId?: string | null;
}

/**
 * Update Dynamic Field DTO
 */
export interface UpdateDynamicField {
  displayName: string;
  description?: string;
  typeConfig?: Record<string, any>;
  validationRules?: Record<string, any>;
  width?: number;
  displayOrder?: number;
  section?: string;
  isVisible?: boolean;
  isReadOnly?: boolean;
  isActive?: boolean;
  isIndexed?: boolean;
  isSearchable?: boolean;
}

/**
 * Dynamic Field Service
 * Service for Dynamic Fields (UDFs) CRUD operations via ABP API
 */
export class DynamicFieldService {
  private readonly baseUrl = '/api/dynamic-fields';

  /**
   * Get all dynamic fields for an entity with optional orgUnitId filtering
   */
  async getFieldsForEntity(
    entityName: string,
    orgUnitId?: string | null
  ): Promise<DynamicField[]> {
    try {
      const params: Record<string, string> = { entityName };
      if (orgUnitId) {
        params.orgUnitId = orgUnitId;
      }
      
      const response = await apiClient.get<DynamicField[]>(
        `${this.baseUrl}/by-entity/${entityName}`,
        { params }
      );
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching dynamic fields for ${entityName}:`, error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get dynamic fields assigned to a specific view
   */
  async getFieldsForView(
    entityName: string,
    viewName: string,
    orgUnitId?: string | null
  ): Promise<DynamicFieldView[]> {
    try {
      const params: Record<string, string> = { entityName, viewName };
      if (orgUnitId) {
        params.orgUnitId = orgUnitId;
      }
      
      // Note: This endpoint needs to be created in DynamicFieldViewController
      // For now, return empty array - views will be handled by backend schema enhancement
      // The backend schema enhancement service handles view-specific field assignment
      return [];
    } catch (error) {
      console.error(`Error fetching dynamic fields for view ${entityName}/${viewName}:`, error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Enhance JSON Schema with UDF fields
   * Calls backend schema enhancement service
   */
  async enhanceJsonSchema(
    baseSchema: JsonSchema,
    entityName: string,
    viewName: string,
    orgUnitId?: string | null
  ): Promise<JsonSchema> {
    try {
      const params: Record<string, any> = {
        entityName,
        viewName
      };
      if (orgUnitId) {
        params.orgUnitId = orgUnitId;
      }

      // Call backend schema enhancement endpoint
      const response = await apiClient.post<JsonSchema>(
        `${this.baseUrl}/schema/enhance`,
        baseSchema as any, // Convert JsonSchema to dictionary for backend
        { params }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error enhancing JSON schema:', error);
      // Return base schema on error
      return baseSchema;
    }
  }

  /**
   * Enhance UI Schema with UDF fields
   * Calls backend schema enhancement service
   */
  async enhanceUiSchema(
    baseUiSchema: UISchemaElement,
    entityName: string,
    viewName: string,
    orgUnitId?: string | null
  ): Promise<UISchemaElement> {
    try {
      const params: Record<string, any> = {
        entityName,
        viewName
      };
      if (orgUnitId) {
        params.orgUnitId = orgUnitId;
      }

      // Call backend schema enhancement endpoint
      const response = await apiClient.post<UISchemaElement>(
        `${this.baseUrl}/schema/enhance-ui`,
        baseUiSchema as any, // Convert UISchemaElement to dictionary for backend
        { params }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error enhancing UI schema:', error);
      // Return base UI schema on error
      return baseUiSchema;
    }
  }

  /**
   * Create a new dynamic field
   */
  async create(data: CreateDynamicField): Promise<DynamicField> {
    try {
      const response = await apiClient.post<DynamicField>(this.baseUrl, data);
      return response.data;
    } catch (error) {
      console.error('Error creating dynamic field:', error);
      throw error;
    }
  }

  /**
   * Update an existing dynamic field
   */
  async update(id: string, data: UpdateDynamicField): Promise<DynamicField> {
    try {
      const response = await apiClient.put<DynamicField>(`${this.baseUrl}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating dynamic field ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a dynamic field
   */
  async delete(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error(`Error deleting dynamic field ${id}:`, error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get a single dynamic field by ID
   */
  async getById(id: string): Promise<DynamicField | undefined> {
    try {
      const response = await apiClient.get<DynamicField>(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching dynamic field ${id}:`, error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get list of dynamic fields with filtering
   */
  async getList(params?: {
    entityName?: string;
    orgUnitId?: string | null;
    includeTenantLevel?: boolean;
    isActive?: boolean;
    skipCount?: number;
    maxResultCount?: number;
  }): Promise<{ items: DynamicField[]; totalCount: number }> {
    try {
      const queryParams: Record<string, any> = {};
      if (params?.entityName) queryParams.entityName = params.entityName;
      if (params?.orgUnitId) queryParams.orgUnitId = params.orgUnitId;
      if (params?.includeTenantLevel !== undefined) queryParams.includeTenantLevel = params.includeTenantLevel;
      if (params?.isActive !== undefined) queryParams.isActive = params.isActive;
      if (params?.skipCount !== undefined) queryParams.skipCount = params.skipCount;
      if (params?.maxResultCount !== undefined) queryParams.maxResultCount = params.maxResultCount;

      const response = await apiClient.get<{ items: DynamicField[]; totalCount: number }>(
        this.baseUrl,
        { params: queryParams }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching dynamic fields list:', error);
      throw new Error(getErrorMessage(error));
    }
  }

}

// Export singleton instance
export const dynamicFieldService = new DynamicFieldService();

